import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import Popup from '../../components/Popup/Popup';
import Button from '../../components/Button/Button';
import Select from '../../components/Select/Select';
import api from '../../config/api';
import compliant from '../../assets/images/compliant.png';
import notCompliant from '../../assets/images/notCompliant.png';
import sortUp from '../../assets/images/sortUp.svg';
import sortDown from '../../assets/images/sortDown.svg';
import css from './ListOfFormsPage.module.css';
import ReactPaginate from 'react-paginate';
import FilterModal from '../../components/FilterModal/FilterModal';
import edit_icon from '../../assets/images/edit.png';

const ListOfFormsPage = () => {
  const [id, setId] = useState();
  const [value, setValue] = useState('all');
  const [forms, setForms] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [formsToSend, setFormsToSend] = useState();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isResume, setIsResume] = useState(false);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [sortBy, setSortBy] = useState({ id: 'id' });
  const [options, setOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formFilters, setFormFilters] = useState([]);
  const navigate = useNavigate();
  const hasMountedRef = useRef(false);
  // SECURITY: Polling interval reference for automatic status updates
  // This allows forms in 'processing' status to automatically update when placement is created
  const pollingIntervalRef = useRef(null);
  // SECURITY: Refs to store latest values for use in polling interval (avoids stale closures)
  const currentValueRef = useRef(value);
  const currentIsPopupOpenRef = useRef(isPopupOpen);
  const currentPageRef = useRef(page);
  // SECURITY: Ref to track if we're currently sorting - prevents useEffect from resetting sort
  const isSortingRef = useRef(false);
  // SECURITY: Ref to store current sort state - persists across re-renders and prevents sort reset
  const currentSortRef = useRef(sortBy);
  
  // Update refs when values change
  useEffect(() => {
    currentValueRef.current = value;
  }, [value]);
  
  useEffect(() => {
    currentIsPopupOpenRef.current = isPopupOpen;
  }, [isPopupOpen]);
  
  useEffect(() => {
    currentPageRef.current = page;
  }, [page]);

  // SECURITY: Update sort ref when sortBy changes - ensures sort state persists
  useEffect(() => {
    currentSortRef.current = sortBy;
  }, [sortBy]);

  // SECURITY: Updated to respect current sort state - prevents sort from being reset
  // If a custom sort is applied, use GET_SORTED_FORMS instead of default sort
  const getData = () => {
    // Check if we have a custom sort applied (not the default 'id' or null)
    const currentSort = currentSortRef.current;
    if (currentSort && currentSort.id && currentSort.id !== 'id') {
      // Use sorted forms API to preserve the current sort (always use descending for consistency)
      api.GET_SORTED_FORMS(`-${currentSort.id}`, currentPageRef.current, 'all').then(response => {
        setLastPage(response.lastPage);
        setForms(
          response.items.map(el => {
            return { ...el, isChecked: false, created_by: el?.created_by ?? '-' };
          }),
        );
      });
    } else {
      // Use default sort (no custom sort applied or sort reset to default)
    api.GET_FORMS(currentPageRef.current).then(response => {
      setLastPage(response.lastPage);
      setForms(
        response.items.map(el => {
          return { ...el, isChecked: false, created_by: el?.created_by ?? '-' };
        }),
      );
    });
    }
  };

  // SECURITY: Updated to respect current sort state - prevents sort from being reset
  // If a custom sort is applied, use GET_SORTED_FORMS instead of default sort
  const getFilteredData = value => {
    // Check if we have a custom sort applied (not the default 'id' or null)
    const currentSort = currentSortRef.current;
    if (currentSort && currentSort.id && currentSort.id !== 'id') {
      // Use sorted forms API to preserve the current sort (always use descending for consistency)
      api.GET_SORTED_FORMS(`-${currentSort.id}`, currentPageRef.current, value).then(response => {
        setLastPage(response.lastPage);
        setForms(
          response.items.map(el => {
            return { ...el, isChecked: false, created_by: el?.created_by ?? '-' };
          }),
        );
      });
    } else {
      // Use default sort (no custom sort applied or sort reset to default)
    api.GET_FILTERED_FORMS(value, currentPageRef.current).then(response => {
      setLastPage(response.lastPage);
      setForms(
        response.items.map(el => {
          return { ...el, isChecked: false, created_by: el?.created_by ?? '-' };
        }),
      );
    });
    }
  };

  useEffect(() => {
    // SECURITY: Skip data fetch if we're currently sorting - prevents sort from being reset
    // The sorting handler will fetch the data with the correct sort order
    if (isSortingRef.current) {
      isSortingRef.current = false; // Reset flag after skipping
      return;
    }
    
    if (isPopupOpen) {
      return;
    }
    
    // SECURITY: getData and getFilteredData now respect the current sort state
    // This ensures that when page/filter changes, the sort is preserved
    if (value === 'all') {
      getData();
    } else {
      getFilteredData(value);
    }
  }, [page, isPopupOpen, value]);

  // SECURITY: Automatic polling for forms with 'processing' status only
  // This ensures the UI updates automatically when forms complete processing
  // without requiring manual browser refresh
  // NOTE: We only poll for 'processing' status, not 'new' status, to reduce server load
  // 'new' forms are drafts that don't need real-time status updates
  useEffect(() => {
    // SECURITY: Don't start polling if we're currently sorting - prevents sort from being reset
    // Wait until sorting is complete before starting/continuing polling
    if (isSortingRef.current) {
      return;
    }
    
    // Check if there are any forms with 'processing' status (not 'new' to reduce resource usage)
    const hasProcessingForms = forms.some(
      form => form.status === 'processing'
    );

    // Only start/continue polling if there are processing forms and no popup is open
    if (hasProcessingForms && !isPopupOpen) {
      // Clear any existing interval first to avoid duplicates
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // SECURITY: Poll every 3 seconds to check for status updates
      // This is frequent enough to feel responsive but not so frequent as to overload the server
      pollingIntervalRef.current = setInterval(() => {
        // SECURITY: Use refs to get latest values (avoids stale closures)
        // Check if popup is open - if so, skip this poll cycle
        if (currentIsPopupOpenRef.current) {
          return;
        }
        
        // SECURITY: Don't poll if we're currently sorting - prevents sort from being reset
        if (isSortingRef.current) {
          return;
        }
        
        // Fetch latest data using current filter value from ref
        // This will update forms state, which will trigger the effect again
        // The effect will then check if we still have processing forms and stop polling if not
        // SECURITY: getData and getFilteredData now respect the current sort state
        if (currentValueRef.current === 'all') {
          getData();
        } else {
          getFilteredData(currentValueRef.current);
        }
      }, 3000); // Poll every 3 seconds
    } else {
      // No processing forms or popup is open - stop polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup: Clear interval when component unmounts or dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [forms, isPopupOpen, value]); // Re-run when forms change or popup state changes

  // SECURITY: Refresh form list when navigating to this page (e.g., after creating a new form)
  // This ensures compliance status is always up-to-date and form-specific bypasses are reflected
  // Each form maintains its own compliance_bypassed status independently
  useEffect(() => {
    // Only refresh on initial mount or when navigating back to this page
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      // Initial load is handled by the other useEffect, so we don't need to call getData here
    }
  }, []);

  useEffect(() => {
    api
      .GET_FORMS_OPTIONS()
      .then(response => {
          setFormFilters(response.form_filters);
          setOptions([{ key: 'all', title: 'All Statuses' }, ...response.booking_form_statuses]);
        },
      );
  }, []);

  const handleSelectChange = e => {
    const value = e.target.value;
    setValue(value);
    setPage(1);
    setLastPage(1);

    if (value === 'all') {
      getData();
      return;
    }

    getFilteredData(value);
  };

  const handleSortForms = (e, search, keys) => {
    // SECURITY: Prevent default browser behavior to avoid page refresh
    // This ensures sorting doesn't trigger form submission or navigation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Handle search case (no event)
    if (!e && search && keys) {
      api.GET_SEARCHED_FORMS('-id', search, keys.join(','), value).then(response => {
          setForms(
            response.map(el => {
              return {
                ...el,
                isChecked: false,
                created_by: el?.created_by ?? '-',
              };
            }),
          );
          setPage(1);
          setLastPage((response.length) / 20);
        },
      );
      return;
    }
    
    // SECURITY: Check if it's a sort click BEFORE calling getData/getFilteredData
    // This prevents unnecessary data fetches and page resets when sorting
    if (
      e.target.tagName === 'INPUT' ||
      e.target.closest('th')?.id === 'compliance'
    ) {
      return;
    }

    // Determine if this is a sort click by checking if we clicked on a sortable column header
    const sortById = e.target.closest('th')?.id;
    const isSortClick = sortById && sortById !== 'compliance' && e.target.tagName !== 'INPUT';
    
    // Only call getData/getFilteredData if it's NOT a sort click
    // If it's a sort click, we'll handle it below and preserve the current page
    if (!isSortClick) {
      if (search === '' && value === 'all') {
        getData();
        return;
      } else {
        getFilteredData(value);
        return;
      }
    }

    // SECURITY: Set sorting flag to prevent useEffect from resetting sort
    // This ensures the sort state is preserved and not overridden by the useEffect
    isSortingRef.current = true;
    
    // SECURITY: Handle sorting - preserve current page and filter, don't reset to page 1
    // This ensures users don't lose their place when sorting
    // Pass the current filter value to maintain the filtered view when sorting
    if (sortBy.id === sortById) {
      // Toggle sort direction (ascending) - preserve current page and filter
      // SECURITY: Update sort ref immediately to prevent race conditions
      currentSortRef.current = { id: null };
      setSortBy({ id: null });
      
      api.GET_SORTED_FORMS(sortById, currentPageRef.current, value).then(response => {
        // SECURITY: Debug logging to verify pagination data (remove in production)
        if (process.env.NODE_ENV === 'development') {
          console.log('Sort response (ascending):', {
            lastPage: response.lastPage,
            itemsCount: response.items?.length,
            currentPage: currentPageRef.current,
            filter: value
          });
        }
        
        // SECURITY: Ensure lastPage is set correctly - use response.lastPage or default to 1
        // This prevents pagination from showing incorrect page count
        setLastPage(response.lastPage || 1);
        setForms(
          response.items.map(el => {
            return {
              ...el,
              isChecked: false,
              created_by: el?.created_by ?? '-',
            };
          }),
      );
        // SECURITY: Reset sorting flag after a short delay to ensure all state updates complete
        // This prevents polling from interfering immediately after sorting
        setTimeout(() => {
          isSortingRef.current = false;
        }, 100);
      });
      return;
    }

    // Sort descending (new sort) - preserve current page and filter
    // SECURITY: Update sort ref immediately to prevent race conditions
    currentSortRef.current = { id: sortById };
    setSortBy({ id: sortById });
    
    api.GET_SORTED_FORMS(`-${sortById}`, currentPageRef.current, value).then(response => {
      // SECURITY: Debug logging to verify pagination data (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('Sort response (descending):', {
          lastPage: response.lastPage,
          itemsCount: response.items?.length,
          currentPage: currentPageRef.current,
          filter: value
        });
      }
      
      // SECURITY: Ensure lastPage is set correctly - use response.lastPage or default to 1
      // This prevents pagination from showing incorrect page count
      setLastPage(response.lastPage || 1);
      setForms(
        response.items.map(el => {
          return { ...el, isChecked: false, created_by: el?.created_by ?? '-' };
        }),
    );
      // SECURITY: Reset sorting flag after a short delay to ensure all state updates complete
      // This prevents polling from interfering immediately after sorting
      setTimeout(() => {
        isSortingRef.current = false;
      }, 100);
    });
  };

  const handleCheckAll = () => {
    if (isAllChecked === false) {
      setIsAllChecked(true);
      return setForms(forms.map(el => ({ ...el, isChecked: true })));
    } else {
      setIsAllChecked(false);
      return setForms(forms.map(el => ({ ...el, isChecked: false })));
    }
  };

  const handleCheckForm = (id) =>
    setForms(
      forms.map(el => {
        if (el.id === id) {
          el.isChecked = !el.isChecked;
          return el;
        }

        return el;
      }),
    );

  const handleOpenArchivePopup = () => {
    if (!forms.find(el => el.isChecked)) {
      return;
    }

    setFormsToSend(forms.filter(el => el.isChecked));
    setIsPopupOpen(true);
    setIsAllChecked(false);
  };

  const handleOpenDeletePopup = () => {
    if (!forms.find(el => el.isChecked)) {
      return;
    }

    setFormsToSend(forms.filter(el => el.isChecked));
    setIsDelete(true);
    setIsPopupOpen(true);
    setIsAllChecked(false);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setIsDelete(false);
    setIsAllChecked(false);
    setIsResume(false);
    setFormsToSend(null);
    setId(null);
  };

  const handleFormClick = (e, id) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') {
      return;
    }
    navigate(`/booking-form/${id}`, { replace: true });
  };

  const handleIsResume = (id) => {
    setId(id);
    setIsResume(true);
    setIsPopupOpen(true);
  };

  const handleGeneratePDF = (id) => {
    api.GET_DOCUMENTS(id).then(
      res =>
        res.status === 'OK' &&
        res.data.map(doc =>
          api.DOWNLOAD_DOCUMENT(id, { filename: doc.name }).then(response => {
            const url = window.URL.createObjectURL(
              new Blob([response], {
                type: 'application/pdf;charset=utf-8',
              }),
            );
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.download_name;
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
            link.remove();
          }),
        ),
    );
  };

  const handlePressFilterModal = (e) => {
    e.stopPropagation();
    setIsModalOpen(!isModalOpen);
  };

  return (
    <>
      {isPopupOpen && (
        <Popup
          isDelete={isDelete}
          isResume={isResume}
          onClose={handlePopupClose}
          users={formsToSend}
          id={id}
          onSuccess={() => {
            // SECURITY: Refresh form list after successful resume to show updated compliance status
            // This ensures the UI reflects the form-specific compliance bypass
            if (value === 'all') {
              getData();
            } else {
              getFilteredData(value);
            }
          }}
        />
      )}
      <main className={css.main}>
        <div className={css.btnsWrapper}>
          <div className={css.flex}>
            <Link className={css.create} to='/booking-form'>
              Create
            </Link>
            <Select
              value={value}
              options={options}
              onChange={handleSelectChange}
            />
          </div>
          <div>
            <div className={css.btnContainer}>
              <Button
                type='delete'
                onClick={handleOpenDeletePopup}
                title='Delete'
              />
            </div>
            <Button
              type='cancel'
              onClick={handleOpenArchivePopup}
              title='Archive'
            />
          </div>
        </div>
        <div className={css.tableWrapper}>
          <table className={css.table}>
            <thead className={css.thead}>
            <tr className={css.trHead} onClick={handleSortForms}>
              <th className={css.thFirst} id='applicant_name'>
                <label className={css.checkboxFirst}>
                  <input
                    className={css.checkbox}
                    type='checkbox'
                    checked={isAllChecked}
                    onChange={handleCheckAll}
                  />
                </label>
                Applicant Name
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'applicant_name' ? sortUp : sortDown}
                />
              </th>
              <th className={css.thSecond} id='applicant_position'>
                Applicant Position
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'applicant_position' ? sortUp : sortDown}
                />
              </th>
              <th className={css.thFormId} id='id'>
                Form id
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'id' ? sortUp : sortDown}
                />
              </th>
              <th className={css.th} id='placement_external_id'>
                Placement ID
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={
                    sortBy.id === 'placement_external_id' ? sortUp : sortDown
                  }
                />
              </th>
              <th className={css.th} id='status'>
                Form Status
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'status' ? sortUp : sortDown}
                />
              </th>
              <th className={css.th} id='updated_at'>
                Last updated
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'updated_at' ? sortUp : sortDown}
                />
              </th>
              <th className={`${css.thCompliance}`} id='compliance'>
                Compliance
              </th>
              <th className={`${css.th}`} id='completed_by'>
                Completed
                <img
                  alt='sorting way'
                  className={css.icon}
                  src={sortBy.id === 'completed_by' ? sortUp : sortDown}
                />
              </th>
              <th className={`${css.thLast} ${css.th5}`} id='created_by'>
                <div className={css.lastContainer}>
                  <div>
                    Created
                    <img
                      alt='sorting way'
                      className={css.icon}
                      src={sortBy.id === 'created_by' ? sortUp : sortDown}
                    />
                  </div>
                  <div className={css.filterContainer} onClick={(e) => handlePressFilterModal(e, 'applicant_name')}>
                    <Button
                      title='Filter'
                      type='filter'
                      onClick={() => {
                      }}
                    />
                  </div>
                  {isModalOpen && (
                    <FilterModal
                      onClose={handlePressFilterModal}
                      onSearch={handleSortForms}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      formFilters={formFilters} />
                  )}
                </div>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td colSpan='9'>
                <div className={css.tbody}>
                  <table className={css.table}>
                    <tbody>
                    {forms.length > 0 &&
                      forms.map(el => (
                        <tr
                          className={css.tr}
                          key={el.id}
                          id={el.id}
                          onClick={(event)=>{
                            handleFormClick(event, el.id);
                          }}
                        >
                          <td className={`${css.tdFirst} ${css.td1}`}>
                            <label>
                              <input
                                className={css.checkbox}
                                type='checkbox'
                                checked={el.isChecked}
                                onChange={handleCheckForm.bind(this, el.id)}
                              />
                            </label>
                            <div className={css.tdNew}>
                              {el.applicant_name}
                            </div>
                          </td>
                          <td>
                            <div className={css.tdNew}>
                              {el.applicant_position}
                            </div>
                          </td>
                          <td className={`${css.td} ${css.td2}`}>
                            {el.id}
                          </td>
                          <td className={`${css.td} ${css.td3}`}>
                            {el.placement_external_id}
                          </td>
                          <td className={`${css.td} ${css.td4Draft}`}>
                            {el.status_name}
                          </td>
                          <td className={`${css.td} ${css.td4Date}`}>
                            {moment(el.updated_at).format('DD/MM/YYYY')}
                          </td>
                          <td className={`${css.td} ${css.td4}`}>
                            {el.is_compliant ? (
                              <div className={css.compliantContainer}>
                                <img
                                  className={css.img}
                                  src={compliant}
                                  alt='compliant'
                                />
                                <p className={css.compliant}>Compliant</p>
                              </div>
                            ) : (
                              <div className={css.compliantContainer}>
                                <img
                                  className={css.img}
                                  src={notCompliant}
                                  alt='not compliant'
                                />
                                <p className={css.notCompliant}>
                                  Not Compliant
                                </p>
                              </div>
                            )}
                          </td>
                          <td className={`${css.td} ${css.td4}`}>
                            <div className={css.completedContainer}>
                              {el.completed_by}
                              {el.edited_by &&
                                <img src={edit_icon} alt='Edit Icon' className={css.editIcon} title={el.edited_by_user} />
                              }
                            </div>

                          </td>
                          <td className={`${css.tdLast} ${css.td5}`}>
                            {el.created_by}

                            {/* SECURITY: Only show Resume button for non-compliant forms that are NOT completed
                                Completed forms (status='sent') should never show Resume button, as compliance
                                was already bypassed when the form was completed */}
                            {!el.is_compliant && el.status !== 'sent' && (
                              <Button
                                type='accent'
                                onClick={handleIsResume.bind(this, el.id)}
                                title='Resume'
                                disabled={el.is_compliant}
                              />
                            )}
                            {el.status === 'sent' && (
                              <Button
                                title='PDFs'
                                type='pdf'
                                onClick={handleGeneratePDF.bind(
                                  this,
                                  el.id,
                                )}
                              />
                            )}
                          </td>

                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
            </tbody>
          </table>

          <ReactPaginate
            breakLabel='...'
            nextLabel='Next →'
            onPageChange={({ selected }) => {
              setPage(selected + 1);
            }}
            pageRangeDisplayed={5}
            pageCount={lastPage}
            initialPage={0}
            previousLabel='← Previous'
            containerClassName={css.pagination}
            marginPagesDisplayed
            previousLinkClassName={css.pagination__link}
            nextLinkClassName={css.pagination__link}
            disabledClassName={css.pagination__link_disabled}
            activeClassName={css.pagination__link_active}
          />
        </div>
      </main>
    </>
  );
};

export default ListOfFormsPage;
